import {Inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {ServerEndpoints} from "../../../server.endpoints";

/**
 * The platform-wide settings that are not an integration credential — the two cards of Owner → **Plattform**.
 *
 * **On the way out**, `null` means "no row is stored". On the way in it means something different — see
 * {@link PlatformSettingsUpdate}, which is the type the write actually takes, precisely so the two cannot be
 * confused at a call site.
 */
export interface PlatformSettings {
  /**
   * The installation's public base URL, without a trailing slash and without a path — the Telegram password
   * bot appends `/static/privacy.html` to it, and it is the only reader.
   */
  domain: string | null;
  /** The register app version the SignalR group model advertises to a connecting device. */
  appVersion: string | null;
  /** The register app build, beside {@link appVersion}. */
  appBuild: string | null;
}

/**
 * What a write may say about each setting. **Three states, not two**, and getting them confused is how a screen
 * silently deletes a value it never showed:
 *
 * | value | meaning |
 * | --- | --- |
 * | `null` (or the field omitted) | **leave this setting exactly as it is** |
 * | `''` | **delete** this setting's row |
 * | any other string | store it (trimmed; `domain` is normalised further) |
 *
 * So a card sends **only its own fields** and leaves the others `null`. That is not an optimisation: the two
 * cards save independently, and a card that echoed the other's last-loaded values would overwrite whatever a
 * second operator changed in between — with the loading screen's blank values, in the window before the first
 * read lands, it would delete them.
 */
export interface PlatformSettingsUpdate {
  domain?: string | null;
  appVersion?: string | null;
  appBuild?: string | null;
}

/** Reads and writes the platform settings (PlatformOwner only). */
@Injectable({providedIn: 'root'})
export class PlatformSettingsHttpService {
  constructor(@Inject("BASE_URL") private baseUrl: string, private httpClient: HttpClient) {
  }

  get(): Observable<PlatformSettings> {
    return this.httpClient.get<PlatformSettings>(
      `${this.baseUrl}${ServerEndpoints.Management_Platform_GetSettings}`);
  }

  /**
   * Applies `update` and answers the **whole** resulting state — including the settings it did not touch, which
   * is what lets one card's save refresh the other card's fields.
   *
   * `domain` must parse as an absolute `http(s)` URL with no path, query or fragment — anything else is refused
   * with `E_PLATFORM_DOMAIN_INVALID`. The server normalises what it accepts (trailing slash stripped, host
   * lower-cased, a default port dropped), so the answer is routinely *not* the string that was sent: render the
   * response, never the request.
   */
  set(update: PlatformSettingsUpdate): Observable<PlatformSettings> {
    return this.httpClient.put<PlatformSettings>(
      `${this.baseUrl}${ServerEndpoints.Management_Platform_SetSettings}`, update);
  }
}
